import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, History, Settings2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell, guardianNav } from "@/components/Shell";
import { Empty, Loader, Pill } from "@/components/Bits";

const statusTone = { drafting: "blue", draft: "blue", active: "green", superseded: "slate", legacy: "slate", failed: "red" };
const LEVELS = [
  ["topic", "Topic"],
  ["chapter", "Chapter"],
  ["subject", "Subject"],
];

export default function GuardianDashboard() {
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/guardian/overview");
      setData(data);
      setLevels((prev) => prev ?? data.thresholds);
    } catch (err) {
      toast.error(errMsg(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (escalation_id) => {
    try {
      await api.post(`/guardian/escalations/${escalation_id}/resolve`);
      toast.success("Marked as handled");
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveThresholds = async () => {    try {
      const { data: res } = await api.post("/guardian/settings", {
        pass_threshold_topic: Number(levels.topic),
        pass_threshold_chapter: Number(levels.chapter),
        pass_threshold_subject: Number(levels.subject),
      });
      setLevels(res);
      toast.success("Pass marks updated");
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!data || !levels) return <Shell nav={guardianNav}><Loader label="Loading guardian dashboard…" /></Shell>;

  const progress = data.progress || {};
  const hasCurriculum = progress.has_curriculum;

  return (
    <Shell nav={guardianNav}>
      <div className="reveal" data-testid="guardian-root">
        <p className="rule-label">Guardian dashboard · read-only oversight</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">{data.student.name}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {data.student.email} · goal: {data.profile?.goal?.type || "not set"}
        </p>

        {data.escalations?.length > 0 && (
          <section className="mt-8 card-flat p-6 !border-amber-300 bg-amber-50/60" data-testid="guardian-escalations">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <h2 className="font-display text-lg font-bold">Where they are stuck</h2>
            </div>
            <p className="mt-1 text-sm text-slate-600">The AI tried three different explanations and still didn't get through. Your student sees this list too.</p>
            <ul className="mt-4 space-y-4">
              {data.escalations.map((e) => (
                <li key={e.escalation_id} className="border border-amber-200 bg-white rounded-xl p-5" data-testid={`escalation-${e.escalation_id}`}>
                  <p className="text-xs text-slate-500">{e.path}</p>
                  <p className="mt-1 font-semibold text-sm">{e.card_title}</p>
                  {e.questions?.length > 0 && (
                    <div className="mt-3">
                      <p className="rule-label">Their questions</p>
                      <ul className="mt-1 space-y-1">
                        {e.questions.map((q, i) => (
                          <li key={i} className="text-sm text-slate-600">
                            “{q}”
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <details className="mt-3">
                    <summary className="text-sm font-semibold text-slate-700 cursor-pointer">See the {(e.attempts || []).length} attempts</summary>
                    <div className="mt-2 space-y-2">
                      {(e.attempts || []).map((a, i) => (
                        <p key={i} className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3 whitespace-pre-wrap">
                          {a}
                        </p>
                      ))}
                    </div>
                  </details>
                  <button
                    data-testid={`escalation-resolve-${e.escalation_id}`}
                    onClick={() => resolve(e.escalation_id)}
                    className="mt-4 px-5 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                  >
                    I've handled this
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-10" data-testid="guardian-active-plan">
          <h2 className="font-display text-2xl font-bold">The plan they're studying</h2>
          <p className="mt-1 text-sm text-slate-500">
            Read-only. Your student owns their plan — nothing here waits on your approval.
          </p>
          {!data.active && (
            <div className="mt-4">
              <Empty title="No path started yet" note="When they start a plan, its subjects, chapters and topics appear here." />
            </div>
          )}
          {data.active && (
            <div className="mt-4 card-flat p-7">
              <div className="flex flex-wrap items-center gap-3">
                <Pill tone="green">active · version {data.active.version}</Pill>
                <span className="font-display text-lg font-semibold">{data.active.label}</span>
                <Pill tone="slate">{data.active.duration_months} months</Pill>
                <Pill tone="slate">~{data.active.daily_topic_minutes} min a day</Pill>
              </div>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">{data.active.rationale}</p>
              <div className="mt-6 space-y-3">
                {(data.active.tree || []).map((subject) => (
                  <div key={subject.subject_id} className="border border-slate-200 rounded-xl p-5">
                    <h4 className="font-display font-semibold">{subject.name}</h4>
                    <p className="mt-1 text-sm text-slate-500">{subject.rationale}</p>
                    <div className="mt-3 space-y-1.5">
                      {subject.chapters.map((chapter) => {
                        const isOpen = open[chapter.chapter_id] ?? false;
                        return (
                          <div key={chapter.chapter_id}>
                            <button
                              onClick={() => setOpen({ ...open, [chapter.chapter_id]: !isOpen })}
                              data-testid={`guardian-chapter-${chapter.chapter_id}`}
                              className="w-full flex items-center gap-2 py-1.5 text-left"
                            >
                              <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                              <span className="text-sm font-medium">{chapter.name}</span>
                              <span className="ml-auto text-xs text-slate-500">
                                {chapter.est_hours} h · {chapter.topics.length} topics
                              </span>
                            </button>
                            {isOpen && (
                              <ul className="ml-6 mb-2 space-y-1">
                                {chapter.topics.map((t) => (
                                  <li key={t.topic_id} className="text-sm text-slate-600">{t.title}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <section className="card-flat p-7" data-testid="guardian-progress">
            <h2 className="font-display text-xl font-bold">Progress overview</h2>
            <p className="text-sm text-slate-500 mt-1">Completion only — no rankings, no streak pressure.</p>
            {hasCurriculum ? (
              <>
                <p className="mt-5 font-display text-4xl font-extrabold">{progress.topics.percent}%</p>
                <p className="text-sm text-slate-500">
                  {progress.topics.completed}/{progress.topics.total} topics · {progress.chapters.completed}/{progress.chapters.total} chapters ·{" "}
                  {progress.subjects.completed}/{progress.subjects.total} subjects
                </p>
                {progress.pace?.message && <p className="mt-3 text-sm text-slate-600">{progress.pace.message}</p>}
                <ul className="mt-5 space-y-3">
                  {(progress.tree || []).map((s) => (
                    <li key={s.subject_id}>
                      <div className="flex justify-between text-sm">
                        <span>{s.name}</span>
                        <span className="text-slate-500">
                          {s.progress.completed}/{s.progress.total}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-teal-600" style={{ width: `${s.progress.percent}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="mt-5 text-sm text-slate-500">No path started yet.</p>
            )}
          </section>

          <div className="space-y-6">
            <section className="card-flat p-7" data-testid="guardian-settings">
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-blue-600" />
                <h2 className="font-display text-xl font-bold">Pass marks</h2>
              </div>
              <p className="text-sm text-slate-500 mt-1">Mastery marks per assessment level. Guidance, not a gate — your student is never blocked.</p>
              <div className="mt-5 space-y-3">
                {LEVELS.map(([key, label]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-sm w-24">{label}</span>
                    <input
                      data-testid={`guardian-threshold-${key}`}
                      type="number"
                      min={40}
                      max={100}
                      value={levels[key]}
                      onChange={(e) => setLevels({ ...levels, [key]: e.target.value })}
                      className="w-24 px-4 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-blue-500"
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                ))}
              </div>
              <button data-testid="guardian-threshold-save" onClick={saveThresholds} className="mt-5 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
                Save pass marks
              </button>
            </section>

            <section className="card-flat p-7" data-testid="guardian-history">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500" />
                <h2 className="font-display text-xl font-bold">Version history</h2>
              </div>
              <ul className="mt-5 divide-y divide-slate-100">
                {data.history.map((c) => (
                  <li key={c.curriculum_id} className="py-3 flex items-center justify-between gap-3">
                    <span className="text-sm">
                      v{c.version} · {c.label}
                    </span>
                    <Pill tone={statusTone[c.status] || "slate"}>{c.status}</Pill>
                  </li>
                ))}
                {!data.history.length && <p className="text-sm text-slate-500">No curriculum drafted yet.</p>}
              </ul>
            </section>

            <Link to="/agents" data-testid="guardian-goto-agents" className="block card-flat p-6 hover:!border-slate-900">
              <p className="font-display font-semibold">Agent prompts</p>
              <p className="text-sm text-slate-500 mt-1">Tune how each agent teaches, plans and assesses.</p>
            </Link>
          </div>
        </div>
      </div>
    </Shell>
  );
}
